// Normalises a devkit's network connections into one shape the UI can render.
//
// Poseidon >= 1.2.1 reports a `network` block on /status with per-connection
// primary / internet / metric data. Older poseidons only give `dev_kit_ips`,
// so we fall back to interface-name sniffing with unknown (null) status.
//
// Each connection:
//   { kind: 'wifi' | 'cellular' | 'ethernet',
//     label, device, ip, metric, primary, internet, extra }
// `primary` / `internet` are true/false when known, null when unknown.

const WIFI_RE = /(?:^|[, ])(wl\w+)\s*:\s*([\d.]+)/;
const CELL_RE = /(?:^|[, ])(wwan\d+)\s*:\s*([\d.]+)/;
const ETH_RE = /(?:^|[, ])(en\w+|eth\d+)\s*:\s*([\d.]+)/i;

const LABELS = { wifi: 'Wi-Fi', cellular: 'Cellular', ethernet: 'Ethernet' };

const firstIp = (arr) => (Array.isArray(arr) && arr.length ? String(arr[0]).split('/')[0] : null);

const fromNetwork = (net) => {
  const routes = Array.isArray(net.default_routes) ? net.default_routes : [];
  const metricFor = (device, fallback) => {
    const r = routes.find((x) => x?.device === device);
    if (r && Number.isFinite(r.metric)) return r.metric;
    return Number.isFinite(fallback) ? fallback : null;
  };
  const primaryDev = net.primary_device || null;
  const out = [];

  if (net.wifi && net.wifi_device) {
    out.push({
      kind: 'wifi',
      label: LABELS.wifi,
      device: net.wifi_device,
      ip: firstIp(net.wifi_ip4),
      metric: metricFor(net.wifi_device, net.wifi_active_metric),
      primary: typeof net.wifi_primary === 'boolean' ? net.wifi_primary : primaryDev === net.wifi_device,
      internet: typeof net.wifi_internet === 'boolean' ? net.wifi_internet : null,
      extra: net.wifi_active_ssid || net.wifi_ssid || null,
    });
  }

  if (net.lte && net.lte_device) {
    out.push({
      kind: 'cellular',
      label: LABELS.cellular,
      device: net.lte_device,
      ip: null,
      metric: metricFor(net.lte_device, net.lte_active_metric),
      primary: typeof net.lte_primary === 'boolean' ? net.lte_primary : primaryDev === net.lte_device,
      internet: typeof net.lte_internet === 'boolean' ? net.lte_internet : null,
      extra: net.signal != null && net.signal !== '' ? `Signal ${net.signal}%` : null,
    });
  }

  const eth = Array.isArray(net.ethernet) ? net.ethernet : [];
  eth
    .filter((e) => e && e.device && (e.connected || e.state === 'connected'))
    .forEach((e) => {
      out.push({
        kind: 'ethernet',
        label: LABELS.ethernet,
        device: e.device,
        ip: firstIp(e.ip4),
        metric: metricFor(e.device, e.active_metric ?? e.route_metric),
        primary: typeof e.primary === 'boolean' ? e.primary : primaryDev === e.device,
        internet: typeof e.internet === 'boolean' ? e.internet : null,
        extra: e.connection || e.profile || null,
      });
    });

  return out;
};

const fromIps = (ips) => {
  if (typeof ips !== 'string') return [];
  const out = [];
  const push = (kind, m) =>
    m && out.push({ kind, label: LABELS[kind], device: m[1], ip: m[2] || null, metric: null, primary: null, internet: null, extra: null });
  push('wifi', ips.match(WIFI_RE));
  push('cellular', ips.match(CELL_RE));
  push('ethernet', ips.match(ETH_RE));
  return out;
};

// Sort: primary first, then by metric ascending (lower = preferred), then fixed kind order.
const KIND_ORDER = { wifi: 0, cellular: 1, ethernet: 2 };
const sortConnections = (list) =>
  [...list].sort((a, b) => {
    if (a.primary !== b.primary) return a.primary ? -1 : 1;
    const am = a.metric ?? Infinity;
    const bm = b.metric ?? Infinity;
    if (am !== bm) return am - bm;
    return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  });

export const getConnections = (versions) => {
  const net = versions?.network;
  const list =
    net && typeof net === 'object' && net.supported !== false
      ? fromNetwork(net)
      : fromIps(versions?.poseidon?.dev_kit_ips);
  return sortConnections(list);
};

// Visual state used for icon colour.
//   'primary'  : the active default route (green)
//   'internet' : has internet, not primary (blue)
//   'none'     : no internet, or unknown (grey)
export const connectionTone = (c) => {
  if (c.primary && c.internet !== false) return 'primary';
  if (c.internet === true) return 'internet';
  return 'none';
};
